import { useEffect, useMemo, useState } from 'react'

import { t } from '../../../common/i18n.js'
import { roundTenth } from '../../../common/leave-accrual.js'
import { countLeaveDays } from '../../../common/leave-days.js'
import { Language } from '../../../common/types/i18n.js'
import { showToast } from '../../toast.js'
import { portalGet, portalPost } from '../../views/portal/utils/api.js'
import { formatDateRange, getInitials } from '../../views/portal/utils/format.js'
import { Button } from '../button/button.js'
import { Select } from '../forms/select/select.js'
import { TextField } from '../forms/text-field/text-field.js'
import { BaseModal } from '../modal/base-modal/base-modal.js'
import {
    DateRange,
    PortalDateRangePicker,
} from '../portal-date-range-picker/portal-date-range-picker.js'
import './portal-book-time-away-modal.css'

// ── Types ──

interface LeavePolicy {
    id: number
    name: string
    leaveTypeName: string
    badgeType: string
}

export interface LeaveBalance {
    policyId: number
    policyName: string
    leaveTypeName: string
    badgeType: string
    totalEntitlement: number
    used: number
    pending: number
    available: number
    carryover: number
}

interface CompanyHoliday {
    id: number
    date: string
    name: string
    year: number
}

interface WorkingPatternAllocation {
    employeeId: number
    workingPattern: string | null
    workingHours: number | null
    standardDayHours: number | null
    effectiveFrom: string
    effectiveTo: string | null
}

interface CalendarEntry {
    id: number
    start_date: string
    end_date: string
    business_days: number
    status: string
    employeeId: number
    firstName: string
    lastName: string
    avatarUrl: string | null
    leaveTypeName: string
    leaveTypeSlug: string
    badgeType: string
    teamNames: string | null
}

interface CalendarApiResponse {
    entries: CalendarEntry[]
    workingPatterns: WorkingPatternAllocation[]
}

interface PortalBookTimeAwayModalProps {
    isOpen: boolean
    onClose: () => void
    myEmployeeId: number | null
    /** Book on behalf of someone else (manager/admin) — null/omitted for self. */
    targetEmployee?: { id: number; name: string } | null
    /** Prefill the period (e.g. a calendar click/drag) — create mode only. */
    initialRange?: DateRange | null
    lang: Language
    onSuccess?: () => void
    /**
     * Edit an existing request instead of creating one — same flow as adding,
     * so overlapping absences stay visible. The type/policy is editable too,
     * pre-selected to the request's current policy.
     */
    editRequest?: {
        id: number
        start_date: string
        end_date: string
        business_days: number
        status: string
        note: string | null
        policyId: number
        leaveTypeName: string
    } | null
    /** Receives the edited fields on submit; the caller owns the PUT + re-approval confirm. */
    onSubmitEdit?: (data: {
        start_date: string
        end_date: string
        note: string
        policy_id: number
    }) => void
}

/**
 * The target employee's policy options, derived from their balance summary —
 * the one endpoint a manager may read already lists every assigned policy.
 */
export const policiesFromBalances = (balances: LeaveBalance[]): LeavePolicy[] =>
    balances.map((b) => ({
        id: b.policyId,
        name: b.policyName,
        leaveTypeName: b.leaveTypeName,
        badgeType: b.badgeType,
    }))

export const PortalBookTimeAwayModal = ({
    isOpen,
    onClose,
    myEmployeeId,
    targetEmployee,
    initialRange,
    lang,
    onSuccess,
    editRequest,
    onSubmitEdit,
}: PortalBookTimeAwayModalProps) => {
    const tOff = t.portal.timeOff
    // Whose absence is being booked — the target when set, otherwise the viewer.
    const subjectId = targetEmployee?.id ?? myEmployeeId

    const [policies, setPolicies] = useState<LeavePolicy[]>([])
    // An empty options list renders no dropdown panel at all, so a silently
    // failed fetch looks like a broken select (TASK-342 follow-up) — surface
    // loading and failure explicitly, with a retry.
    const [policiesLoad, setPoliciesLoad] = useState<'loading' | 'error' | 'ready'>('loading')
    const [policiesRetryToken, setPoliciesRetryToken] = useState(0)
    const [balances, setBalances] = useState<LeaveBalance[]>([])
    const [holidays, setHolidays] = useState<CompanyHoliday[]>([])
    const [entries, setEntries] = useState<CalendarEntry[]>([])

    const [formPolicyId, setFormPolicyId] = useState<number | ''>('')
    const [dateRange, setDateRange] = useState<DateRange | null>(null)
    const [formNote, setFormNote] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Reset form states when modal opens/closes; pre-fill from the request
    // being edited so the edit flow opens with its current values, or from the
    // caller-provided period (calendar click/drag) when creating.
    useEffect(() => {
        if (!isOpen) {
            setFormPolicyId('')
            setDateRange(null)
            setFormNote('')
        } else if (editRequest) {
            setFormPolicyId(editRequest.policyId)
            setDateRange({ start: editRequest.start_date, end: editRequest.end_date })
            setFormNote(editRequest.note ?? '')
        } else if (initialRange) {
            setDateRange(initialRange)
        }
    }, [isOpen, editRequest, initialRange])

    // Load policies and balances — the target's when booking on behalf (their
    // time-off endpoint carries both), otherwise the viewer's own. Loaded in
    // edit mode too so the type can be reclassified from the same dropdown.
    useEffect(() => {
        if (!isOpen) return

        let active = true
        setPoliciesLoad('loading')
        void (async () => {
            if (targetEmployee) {
                const res = await portalGet<{ balances: LeaveBalance[] }>(
                    `/api/portal/employees/${targetEmployee.id}/time-off`,
                )
                if (!active) return
                if (!res.ok) {
                    setPoliciesLoad('error')
                    return
                }
                setPolicies(policiesFromBalances(res.data.balances))
                setBalances(res.data.balances)
                setPoliciesLoad('ready')
                return
            }
            const [policiesRes, balancesRes] = await Promise.all([
                portalGet<LeavePolicy[]>('/api/portal/leave-policies'),
                portalGet<{ balances: LeaveBalance[] }>('/api/portal/me/leave-balance'),
            ])
            if (!active) return
            if (!policiesRes.ok) {
                setPoliciesLoad('error')
                return
            }
            setPolicies(policiesRes.data)
            if (balancesRes.ok) setBalances(balancesRes.data.balances)
            setPoliciesLoad('ready')
        })()

        return () => {
            active = false
        }
    }, [isOpen, targetEmployee, editRequest, policiesRetryToken])

    // Load holidays (current and next year)
    useEffect(() => {
        if (!isOpen) return

        const currentYear = new Date().getFullYear()
        const nextYear = currentYear + 1

        void (async () => {
            const [currentRes, nextRes] = await Promise.all([
                portalGet<CompanyHoliday[]>(`/api/portal/company-holidays?year=${currentYear}`),
                portalGet<CompanyHoliday[]>(`/api/portal/company-holidays?year=${nextYear}`),
            ])
            const combined: CompanyHoliday[] = []
            if (currentRes.ok) combined.push(...currentRes.data)
            if (nextRes.ok) combined.push(...nextRes.data)
            setHolidays(combined)
        })()
    }, [isOpen])

    // Load entries & working patterns for date range
    useEffect(() => {
        if (!isOpen || !dateRange || !subjectId) {
            setEntries([])
            return
        }

        let active = true
        void (async () => {
            const res = await portalGet<CalendarApiResponse>(
                `/api/portal/time-off/calendar?start=${dateRange.start}&end=${dateRange.end}`,
            )
            if (active && res.ok) {
                setEntries(res.data.entries)
            }
        })()

        return () => {
            active = false
        }
    }, [isOpen, dateRange, subjectId])

    // Estonian leave is counted in calendar days minus public holidays
    // (weekends count, FTE/working-pattern doesn't reduce it) — the exact rule
    // the server deducts, via the shared helper, so the preview always matches.
    const requestedDays = dateRange
        ? countLeaveDays(
              dateRange.start,
              dateRange.end,
              holidays.map((h) => h.date),
          )
        : 0
    const selectedBalance = formPolicyId
        ? balances.find((b) => b.policyId === formPolicyId)
        : undefined
    // Only show the "you'll have N left" preview for balance-tracked policies
    // (a real entitlement). Request-and-log types (sick, events, …) have no
    // balance, so showing a negative remainder would be misleading.
    // Round the difference to the ledger's 0.1-day granularity — `available` is
    // already server-rounded, but float subtraction (e.g. 9.2 − 3) re-introduces
    // noise like 6.199999999999999 that would otherwise render verbatim.
    //
    // When editing, the request's own days are already committed inside
    // `available` (deducted if approved, counted as pending otherwise) and the
    // server refunds them before re-checking — credit them back here or the
    // preview double-counts the unchanged days. Only on the request's original
    // policy: reclassifying moves the days to a different balance.
    const editedRequestDays =
        (editRequest?.status === 'APPROVED' || editRequest?.status === 'PENDING') &&
        editRequest.policyId === formPolicyId
            ? editRequest.business_days
            : 0
    const remainingDays =
        selectedBalance && selectedBalance.totalEntitlement > 0
            ? roundTenth(selectedBalance.available + editedRequestDays - requestedDays)
            : null

    // Everyone (other than the subject) who is away during the selected range
    const alsoAway = useMemo(() => {
        if (!dateRange || !subjectId) return []
        const list: {
            employeeId: number
            firstName: string
            lastName: string
            avatarUrl: string | null
            start: string
            end: string
        }[] = []
        const byPerson = new Map<number, (typeof list)[number]>()
        for (const e of entries) {
            if (e.employeeId === subjectId) continue
            if (!(e.start_date <= dateRange.end && e.end_date >= dateRange.start)) continue
            const existing = byPerson.get(e.employeeId)
            if (existing) {
                if (e.start_date < existing.start) existing.start = e.start_date
                if (e.end_date > existing.end) existing.end = e.end_date
            } else {
                byPerson.set(e.employeeId, {
                    employeeId: e.employeeId,
                    firstName: e.firstName,
                    lastName: e.lastName,
                    avatarUrl: e.avatarUrl,
                    start: e.start_date,
                    end: e.end_date,
                })
            }
        }
        list.push(...byPerson.values())
        return list
    }, [dateRange, subjectId, entries])

    // The subject's own bookings overlapping the selected range. Surfaced as a
    // warning because the server rejects overlapping requests — the alsoAway list
    // above deliberately covers other people only, so without this the viewer
    // gets no hint that the subject is already away then.
    const myOverlap = useMemo(() => {
        if (!dateRange || !subjectId) return []
        return entries.filter(
            (e) =>
                e.employeeId === subjectId &&
                // When editing, the request itself is not an overlap.
                e.id !== editRequest?.id &&
                e.start_date <= dateRange.end &&
                e.end_date >= dateRange.start,
        )
    }, [dateRange, subjectId, entries, editRequest])

    const handleSubmit = async () => {
        // Edit mode: hand the edited fields back to the caller, which owns
        // the PUT and the re-approval confirm flow.
        if (editRequest) {
            if (!dateRange || !onSubmitEdit || !formPolicyId) return
            onSubmitEdit({
                start_date: dateRange.start,
                end_date: dateRange.end,
                note: formNote,
                policy_id: formPolicyId,
            })
            onClose()
            return
        }

        if (!formPolicyId || !dateRange) return

        if (!subjectId) {
            showToast(tOff.employeeProfileMissing(lang), 'error')
            return
        }

        setIsSubmitting(true)
        const result = await portalPost('/api/portal/time-off/requests', {
            employee_id: subjectId,
            policy_id: formPolicyId,
            start_date: dateRange.start,
            end_date: dateRange.end,
            note: formNote || undefined,
        })

        if (result.ok) {
            onClose()
            setFormPolicyId('')
            setDateRange(null)
            setFormNote('')
            onSuccess?.()
        } else {
            showToast(result.error, 'error')
        }
        setIsSubmitting(false)
    }

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            disableBackdropClose={isSubmitting}
            className="portal-book-time-away-modal"
            showClose
            closeAriaLabel={tOff.close(lang)}
        >
            <div className="portal-book-time-away-modal__header">
                <h3 className="portal-book-time-away-modal__title">
                    {editRequest
                        ? tOff.editTimeAwayTitle(lang)
                        : targetEmployee
                          ? tOff.bookTimeAwayFor(targetEmployee.name)(lang)
                          : tOff.bookTimeAway(lang)}
                </h3>
            </div>

            {dateRange && (
                <div className="portal-book-time-away-modal__business-days">
                    {tOff.requestingDays(requestedDays)(lang)}
                    {remainingDays !== null && ` — ${tOff.daysLeft(remainingDays)(lang)}`}
                </div>
            )}

            <div className="portal-book-time-away-modal__form-group">
                <label className="portal-book-time-away-modal__form-label">
                    {tOff.period(lang)}
                </label>
                <PortalDateRangePicker
                    asField
                    value={dateRange}
                    onChange={setDateRange}
                    lang={lang}
                    placeholder={tOff.periodPlaceholder(lang)}
                />
            </div>

            <div className="portal-book-time-away-modal__form-group">
                <label className="portal-book-time-away-modal__form-label">
                    {tOff.reason(lang)}
                </label>
                <Select
                    className="portal-book-time-away-modal__form-select"
                    placeholder="—"
                    isLoading={policiesLoad === 'loading'}
                    value={formPolicyId === '' ? '' : String(formPolicyId)}
                    onChange={(value) => setFormPolicyId(value ? Number(value) : '')}
                    options={policies.map((p) => ({
                        value: String(p.id),
                        // The type name, not the policy name — it's what admins
                        // rename in Settings, while the policy name can lag behind
                        // (TASK-400). It also carries no "(28 päeva)" entitlement
                        // suffix, which would only confuse when picking a reason.
                        label: p.leaveTypeName,
                    }))}
                />
                {policiesLoad === 'error' && (
                    <div className="portal-book-time-away-modal__form-error">
                        {tOff.typesLoadFailed(lang)}{' '}
                        <button
                            type="button"
                            className="portal-book-time-away-modal__form-error-retry"
                            onClick={() => setPoliciesRetryToken((n) => n + 1)}
                        >
                            {tOff.retry(lang)}
                        </button>
                    </div>
                )}
            </div>

            <div className="portal-book-time-away-modal__form-group">
                <label className="portal-book-time-away-modal__form-label">{tOff.note(lang)}</label>
                <TextField
                    as="textarea"
                    className="portal-book-time-away-modal__form-textarea"
                    value={formNote}
                    onChange={(value) => setFormNote(value)}
                    placeholder={tOff.notePlaceholder(lang)}
                />
            </div>

            {myOverlap.length > 0 && (
                <div className="portal-book-time-away-modal__self-overlap">
                    <div className="portal-book-time-away-modal__self-overlap-title">
                        {tOff.selfOverlapWarning(lang)}
                    </div>
                    {myOverlap.map((entry) => (
                        <div
                            key={entry.id}
                            className="portal-book-time-away-modal__self-overlap-row"
                        >
                            {entry.leaveTypeName}:{' '}
                            {formatDateRange(entry.start_date, entry.end_date, lang)}
                        </div>
                    ))}
                </div>
            )}

            {alsoAway.length > 0 && (
                <div className="portal-book-time-away-modal__also-away">
                    <div className="portal-book-time-away-modal__also-away-title">
                        {tOff.alsoAway(alsoAway.length)(lang)}
                    </div>
                    {alsoAway.map((person) => (
                        <div
                            key={person.employeeId}
                            className="portal-book-time-away-modal__also-away-row"
                        >
                            <div className="portal-book-time-away-modal__avatar">
                                {person.avatarUrl ? (
                                    <img src={person.avatarUrl} alt="" />
                                ) : (
                                    getInitials(person.firstName, person.lastName)
                                )}
                            </div>
                            <span className="portal-book-time-away-modal__also-away-name">
                                {person.firstName} {person.lastName}
                            </span>
                            <span className="portal-book-time-away-modal__also-away-range">
                                {formatDateRange(person.start, person.end, lang)}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <Button
                className="portal-book-time-away-modal__submit"
                size="medium"
                variant="primary"
                text={
                    isSubmitting
                        ? tOff.submitting(lang)
                        : editRequest
                          ? tOff.editConfirm(lang)
                          : tOff.requestTimeAway(lang)
                }
                isDisabled={isSubmitting || (!editRequest && !formPolicyId) || !dateRange}
                onClick={() => void handleSubmit()}
            />
        </BaseModal>
    )
}
